import { useEffect, useRef, useState, useCallback } from 'react';
import { useWordStore } from '../stores/wordStore';
import { api } from '../services/api';

interface DraftData {
  timestamp: string;
  activeWordId: string;
  activeStage: string;
  cardEdits: Record<string, unknown>;
}

const DRAFT_KEY = 'waw_session_draft';
const SAVE_INTERVAL_MS = 30_000;
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

// Self-healing equivalence for draft cardEdit values vs current Airtable
// values. Treats null / undefined / "" as the same empty state and falls
// back to JSON-stringify for arrays/objects. Used to silently drop drafts
// whose every cardEdit already matches what's saved server-side — those
// are false positives from the pre-fix dirty-tracking era.
function valuesEquivalent(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if ((a == null || a === '') && (b == null || b === '')) return true;
  if (typeof a !== typeof b) return String(a) === String(b);
  return JSON.stringify(a) === JSON.stringify(b);
}

export function useSessionDraft() {
  const { selectedWordId, selectedWord, dirtyFields, selectWord } = useWordStore();
  const [pendingDraft, setPendingDraft] = useState<DraftData | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Check for saved draft on mount. If a draft exists, fetch the word's
  // current Airtable state and compare every cardEdit to its server value.
  // If all match, the draft is stale (false positive) — drop it silently
  // without showing the banner. If the API call fails, fall back to the
  // banner conservatively so a real recovery isn't dropped.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      let draft: DraftData | null = null;
      try {
        const raw = localStorage.getItem(DRAFT_KEY);
        if (!raw) return;
        const parsed = JSON.parse(raw) as DraftData;
        if (!parsed.timestamp) return;
        const age = Date.now() - new Date(parsed.timestamp).getTime();
        if (age > MAX_AGE_MS) {
          localStorage.removeItem(DRAFT_KEY);
          return;
        }
        draft = parsed;
      } catch {
        localStorage.removeItem(DRAFT_KEY);
        return;
      }

      const cardEdits = draft.cardEdits || {};
      const cardEditKeys = Object.keys(cardEdits);
      if (cardEditKeys.length > 0 && draft.activeWordId) {
        try {
          const data = await api.get(`/api/words/${draft.activeWordId}`) as { word?: Record<string, unknown> };
          const current = data?.word;
          if (current) {
            const allMatch = cardEditKeys.every((field) =>
              valuesEquivalent(cardEdits[field], current[field])
            );
            if (allMatch) {
              localStorage.removeItem(DRAFT_KEY);
              return;
            }
          }
        } catch {
          // API unavailable — fall through and surface the banner.
        }
      }

      if (!cancelled) setPendingDraft(draft);
    })();
    return () => { cancelled = true; };
  }, []);

  // Auto-save interval
  useEffect(() => {
    timerRef.current = setInterval(() => {
      const store = useWordStore.getState();
      if (!store.selectedWordId || !store.selectedWord || !store.dirtyFields || store.dirtyFields.size === 0) return;

      const cardEdits: Record<string, unknown> = {};
      for (const field of store.dirtyFields) {
        cardEdits[field] = store.selectedWord[field];
      }

      const draft = {
        timestamp: new Date().toISOString(),
        activeWordId: store.selectedWordId,
        activeStage: store.selectedWord._stage,
        cardEdits,
      };

      localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
    }, SAVE_INTERVAL_MS);

    return () => { if (timerRef.current !== null) clearInterval(timerRef.current); };
  }, []);

  const restoreDraft = useCallback(async () => {
    if (!pendingDraft) return;
    const targetWordId = pendingDraft.activeWordId;
    const edits = pendingDraft.cardEdits;
    try {
      const store = useWordStore.getState();
      await store.selectWord(targetWordId);

      // Wait for word to load, then apply edits — but only if the SAME word is
      // still selected. Without this guard a fast word switch during the 500ms
      // window applied the draft's edits to whatever word happened to be open
      // (2026-07-05 L18 fix).
      setTimeout(() => {
        const s = useWordStore.getState();
        if (!s.selectedWord) return;
        if (s.selectedWordId !== targetWordId) return;
        if (edits) {
          for (const [field, value] of Object.entries(edits)) {
            s.updateWordField(field, value);
          }
        }
      }, 500);
    } catch {
      // word may no longer exist
    }
    // Clear the persisted draft on restore so a later mount doesn't re-offer an
    // already-restored draft (2026-07-05 L18 fix).
    localStorage.removeItem(DRAFT_KEY);
    setPendingDraft(null);
  }, [pendingDraft]);

  const discardDraft = useCallback(() => {
    localStorage.removeItem(DRAFT_KEY);
    setPendingDraft(null);
  }, []);

  return { pendingDraft, restoreDraft, discardDraft } as const;
}

export function clearSessionDraft(): void {
  localStorage.removeItem(DRAFT_KEY);
}
